// Supabase answers at most ~1000 rows per request and does not say when it stopped early. A figure
// that is ADDED UP from rows (a report total, what has been matched) must never be built from a
// silently truncated list, so those queries fetch every page.

export const PAGE_SIZE = 1000

type Page<T> = PromiseLike<{ data: T[] | null; error: unknown }>

/**
 * Read every row a query can return, one page at a time, in the order the query asks for. The query
 * MUST be ordered by something unique, or rows can repeat or go missing between pages. Returns the
 * same { data, error } shape as a single query so callers can unwrap it the usual way.
 */
export async function fetchAllRows<T>(
  page: (from: number, to: number) => Page<T>,
  pageSize = PAGE_SIZE,
): Promise<{ data: T[] | null; error: unknown }> {
  const all: T[] = []
  for (let from = 0; ; from += pageSize) {
    const res = await page(from, from + pageSize - 1)
    if (res.error) return { data: null, error: res.error }
    const rows = res.data ?? []
    all.push(...rows)
    if (rows.length < pageSize) return { data: all, error: null }
  }
}
